<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { ElNotification } from 'element-plus';
import { assetUrl } from '@/utils/assetUrl';
import AppHeader from '@/components/layout/AppHeader.vue';
import PlayerLevelBar from '@/components/layout/PlayerLevelBar.vue';
// AppSidebar retiree (phase 2 refonte nav, cf commentaire dans le template).
import MobileBottomNav from '@/components/layout/MobileBottomNav.vue';
import InboxModal from '@/components/inbox/InboxModal.vue';
import InventoryModal from '@/components/inventory/InventoryModal.vue';
import RewardChestsModal from '@/components/rewards/RewardChestsModal.vue';
import QuestsModal from '@/components/quests/QuestsModal.vue';
import ResearchGlobalModal from '@/components/research/ResearchGlobalModal.vue';
import ChatModal from '@/components/chat/ChatModal.vue';
import SpatialBreadcrumb from '@/components/layout/SpatialBreadcrumb.vue';
import HarmonicOrbitalBanner from '@/components/layout/HarmonicOrbitalBanner.vue';
import { useResourceSummaryStore } from '@/stores/resourceSummary.store';
import { useInboxStore } from '@/stores/inbox.store';
import { useQuestsStore } from '@/stores/quests.store';
import { useChatUnreadStore } from '@/stores/chat.store';
import { useRewardChestsStore } from '@/stores/rewardChests.store';
import { useInventoryNewStore } from '@/stores/inventoryNew.store';
import { useResearchStore } from '@/stores/research.store';
import { useAuthStore } from '@/stores/auth.store';
import { useSessionStore } from '@/stores/session.store';
import { useAllianceHub } from '@/composables/useAllianceHub';
import { useTaskHub } from '@/composables/useTaskHub';

const route = useRoute();

// Fond d'ambiance discret par page (image `ambiance`, derrière le contenu).
// Les pages avec leur propre rendu visuel (globe planète, scène 3D cosmos) ne
// reçoivent PAS de fond -> ambianceCode renvoie null. Codes = AMBIANCE_PROMPTS.
const AMBIANCE_BY_ROUTE: Record<string, string> = {
  home: 'dashboard_hero',
  'my-stats': 'dashboard_hero',
  research: 'research',
  'research-global': 'research',
  'fleet-index': 'fleet_battle',
  fleet: 'fleet_battle',
  'ship-designer': 'shipyard',
  academy: 'academy',
  inbox: 'comms',
  chat: 'comms',
  'battle-index': 'victory',
  battle: 'victory',
  'battle-replay': 'victory',
  'battle-report': 'victory',
  wrecks: 'wrecks',
  'spy-reports': 'espionage',
  stations: 'station',
  creatures: 'bestiary',
  glyphs: 'glyphs',
  quests: 'quests',
  achievements: 'achievements',
  alliance: 'alliance_war',
  blueprints: 'blueprints',
  inventory: 'inventory',
  museum: 'museum',
  market: 'market',
  news: 'news',
  leaderboard: 'leaderboard',
};

const ambianceUrl = computed<string | null>(() => {
  const code = AMBIANCE_BY_ROUTE[String(route.name ?? '')];
  return code ? assetUrl('ambiance', code, 0) : null;
});

// Vue planète : la fiche gère sa propre mise en page (header sticky, barre de
// modules, conteneur). On supprime le padding vertical du <el-main> pour coller
// le header sous le breadcrumb (haut) et utiliser toute la hauteur (bas). Le
// padding horizontal est conservé. Scopé à cette route -> les autres vues
// gardent leur marge habituelle.
const isPlanetView = computed(() => route.name === 'planet');

const resourceSummary = useResourceSummaryStore();
const inbox = useInboxStore();
const quests = useQuestsStore();
const chatUnread = useChatUnreadStore();
const rewardChests = useRewardChestsStore();
const inventoryNew = useInventoryNewStore();
const research = useResearchStore();
const auth = useAuthStore();
const session = useSessionStore();
const allianceHub = useAllianceHub();
const taskHub = useTaskHub();
const taskUnsubs: Array<() => void> = [];

// Handlers SignalR alliance : on stocke les unsubscribers pour
// nettoyer en onUnmounted. Toasts via ElNotification (top-right, dure
// 5s) + refresh inboxStore pour decrement immediat des badges quand
// l'utilisateur agit ailleurs.
const allianceUnsubs: Array<() => void> = [];

onMounted(() => {
  // Démarre le polling auto-refresh de la barre de ressources globale.
  // S'exécute tant que le joueur est dans le layout game (toutes routes :
  // /planet, /galaxy, /fleets, /research, etc.).
  resourceSummary.start();
  // Polling des compteurs alimentant les badges (Inbox 60s, Chat 45s,
  // Quetes 90s). Pris en charge sur HeaderGlobalNav + MobileBottomNav.
  inbox.start();
  chatUnread.start();
  quests.start();

  // SignalR alliance : init si auth + souscriptions aux 4 events.
  if (auth.user) {
    void allianceHub.init((session.activePlayerUniverseId ?? 0));
    allianceUnsubs.push(allianceHub.onAllianceInvitation((p) => {
      ElNotification({
        title: 'Invitation alliance',
        message: `[${p.allianceTag}] ${p.allianceName} vous invite a rejoindre.`,
        type: 'info', duration: 6000,
      });
      void inbox.refresh();
    }));
    allianceUnsubs.push(allianceHub.onTreatyProposal((p) => {
      ElNotification({
        title: 'Proposition de traite',
        message: `[${p.fromTag}] ${p.fromName} propose ${p.kind}.`,
        type: 'info', duration: 6000,
      });
      void inbox.refresh();
    }));
    allianceUnsubs.push(allianceHub.onTreatyAccepted((p) => {
      ElNotification({
        title: `Traite ${p.kind} signe`,
        message: `[${p.partnerTag}] ${p.partnerName} a signe votre proposition.`,
        type: 'success', duration: 6000,
      });
      void inbox.refresh();
    }));
    allianceUnsubs.push(allianceHub.onTreatyBroken((p) => {
      ElNotification({
        title: `Traite ${p.kind} rompu`,
        message: `[${p.partnerTag}] ${p.partnerName} a rompu le traite.`
          + (p.casusBelli ? ` Motif : ${p.casusBelli}` : ''),
        type: 'warning', duration: 8000,
      });
      void inbox.refresh();
    }));
    allianceUnsubs.push(allianceHub.onMemberJoined((p) => {
      ElNotification({
        title: `[${p.allianceTag}] Nouveau membre`,
        message: `${p.newMemberLogin} a rejoint l'alliance.`,
        type: 'success', duration: 5000,
      });
      // Le backend a aussi persiste un InboxMessage System pour
      // l'historique -- refresh badge.
      void inbox.refresh();
    }));
    allianceUnsubs.push(allianceHub.onMemberLeft((p) => {
      ElNotification({
        title: `[${p.allianceTag}] Depart`,
        message: `${p.leftMemberLogin} a quitte l'alliance (etait ${p.wasRank}).`,
        type: 'info', duration: 5000,
      });
      void inbox.refresh();
    }));
    allianceUnsubs.push(allianceHub.onMemberRankChanged((p) => {
      const isMe = p.targetPuId === (session.activePlayerUniverseId ?? 0);
      ElNotification({
        title: isMe
          ? `Votre rang dans [${p.allianceTag}] a change`
          : `[${p.allianceTag}] Rang modifie`,
        message: isMe
          ? `Vous etes maintenant ${p.newRank}.`
          : `${p.targetLogin} est maintenant ${p.newRank}.`,
        type: isMe ? 'warning' : 'info',
        duration: isMe ? 8000 : 4000,
      });
      void inbox.refresh();
    }));

    // SignalR tâches : push temps-réel des complétions (build/recherche/prod).
    // Au niveau layout on se contente de rafraîchir la barre de ressources
    // (une construction terminée change la production). La vue planète, elle,
    // s'abonne aussi pour recharger sa tranche (cf PlanetHomeView).
    void taskHub.init((session.activePlayerUniverseId ?? 0));
    taskUnsubs.push(taskHub.onTaskResolved(() => {
      // Une tâche résolue (build/recherche) crédite des points de progression :
      // resourceSummary porte désormais le niveau/score -> ce refresh met aussi
      // la barre de niveau à jour (dernier gain surligné).
      void resourceSummary.refresh();
      // Temps-réel des notifications : une tâche résolue peut avoir débloqué un
      // achievement ou terminé une quête (messages System). On rafraîchit l'inbox
      // -> son toast ElNotification (dédoublonné via lastMaxSystemId) s'affiche
      // immédiatement au lieu d'attendre le poll 60s.
      void inbox.refresh();
      // Coffres de progression : franchir un palier (niveau/rang) pendant un tick
      // octroie un coffre -> refresh du badge + toast si le total a augmenté.
      const beforeChests = rewardChests.count;
      void rewardChests.refresh().then(() => {
        const gained = rewardChests.count - beforeChests;
        if (gained > 0) {
          ElNotification({
            title: '🎁 Coffre de récompense !',
            message: `Tu as reçu ${gained} coffre(s) de progression. Ouvre-les depuis « Récompenses ».`,
            type: 'success', duration: 6000,
          });
        }
      });
      // Un tick peut avoir crédité du butin (fouille/combat/récolte) : on
      // rafraîchit le compteur d'objets « nouveaux » du menu Inventaire.
      void inventoryNew.refresh();
      // Une recherche a pu démarrer/finir -> indicateur « nouvelle recherche ».
      void research.refreshGlobalCount();
    }));
  }
});
onUnmounted(() => {
  // Stoppe le polling quand on sort du layout game (logout, retour au lobby,
  // accès à /admin). Les stores conservent leurs dernières valeurs en mémoire
  // pour un retour rapide sur le layout.
  resourceSummary.stop();
  inbox.stop();
  chatUnread.stop();
  quests.stop();
  for (const u of allianceUnsubs) u();
  allianceUnsubs.length = 0;
  void allianceHub.disconnect();
  for (const u of taskUnsubs) u();
  taskUnsubs.length = 0;
  void taskHub.disconnect();
});
</script>

<template>
  <!-- Fond d'ambiance plein écran, fixe, derrière tout le contenu. Voile
       sombre par-dessus pour garder le contenu lisible. -->
  <div class="app-ambiance" aria-hidden="true">
    <Transition name="ambiance-fade">
      <div
        v-if="ambianceUrl"
        :key="ambianceUrl"
        class="app-ambiance-img"
        :style="{ backgroundImage: `url(${ambianceUrl})` }"
      />
    </Transition>
    <div class="app-ambiance-veil" />
  </div>

  <el-container class="app-layout">
    <!-- Phase 2 refonte nav : sidebar retiree. Les destinations meta
         (Messages, Quetes, Recherches, Inventaire, Chat + dropdown Plus)
         sont desormais dans HeaderGlobalNav (slotee dans AppHeader).
         Les actions contextuelles a la planete sont dans PlanetShortcuts.
         L'AppSidebar reste importable au cas ou on voudrait la reactiver. -->
    <el-container>
      <!-- height="auto" : la barre de ressources peut wrapper sur 2 lignes
           si l'ecran est etroit ou s'il y a beaucoup de chips. AppHeader
           garde un min-height de 56px pour rester homogene quand non-wrap. -->
      <el-header height="auto" class="app-layout-header">
        <AppHeader />
      </el-header>

      <!-- Barre de niveau du joueur (sous le menu global) : badge de tranche,
           progression vers le niveau suivant, dernier gain en surbrillance. -->
      <PlayerLevelBar />

      <!-- Fin de jeu : bannière Harmonic Orbital (découverte -> activation).
           Rend vide tant que l'objet n'est pas découvert dans l'univers. -->
      <HarmonicOrbitalBanner />

      <!-- Breadcrumb spatial (Univers > Galaxie > Systeme > ...).
           Rend vide sur les routes meta (inbox, recherches, classement). -->
      <SpatialBreadcrumb />

      <el-main class="app-layout-main" :class="{ 'app-layout-main--flush': isPlanetView }">
        <router-view />
      </el-main>
    </el-container>

    <!-- Lot 35D — bottom-nav mobile (visible <768px via CSS) -->
    <MobileBottomNav class="app-layout-bottom-nav" />

    <!-- Overlays globaux : Messages + Inventaire ouverts depuis la nav
         (overlays.store) au lieu d'une navigation pleine page. -->
    <InboxModal />
    <InventoryModal />
    <RewardChestsModal />
    <QuestsModal />
    <ResearchGlobalModal />
    <ChatModal />
  </el-container>
</template>

<style lang="scss" scoped>
// Fond d'ambiance : couche fixe pleine fenêtre. Base sombre opaque (couvre le
// viewport quelle que soit la page) + image atténuée + voile dégradé.
.app-ambiance {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: $bg-primary;
}
.app-ambiance-img {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0.16;
}
.app-ambiance-veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(8, 12, 20, 0.55) 0%, rgba(8, 12, 20, 0.84) 100%);
}
// Fondu doux au changement de page.
.ambiance-fade-enter-active,
.ambiance-fade-leave-active { transition: opacity 0.6s ease; }
.ambiance-fade-enter-from,
.ambiance-fade-leave-to { opacity: 0; }

.app-layout {
  position: relative;
  z-index: 1;
  min-height: 100vh;
  // Transparent : laisse voir le fond d'ambiance fixe derrière (.app-ambiance).
  background: transparent;
}

.app-layout-header {
  padding: 0;
}

.app-layout-main {
  padding: $space-6;
  @include scrollbar-thin;

  @media (max-width: 767.99px) {
    padding: $space-3;
    // Laisse de la place pour la bottom-nav (~64px + safe-area iOS)
    padding-bottom: calc(64px + env(safe-area-inset-bottom));
  }

  // Vue planète : pleine largeur ET pleine hauteur (header collé sous le
  // breadcrumb). On annule TOUT le padding du <main> -> le contenu occupe toute
  // la largeur ; les sections internes (top-bar, .buildings-main) gardent leur
  // propre padding $space-4 comme gouttière. Sur mobile, on conserve la marge
  // basse pour la bottom-nav.
  &.app-layout-main--flush {
    padding: 0;

    @media (max-width: 767.99px) {
      padding-bottom: calc(64px + env(safe-area-inset-bottom));
    }
  }
}

.app-layout-bottom-nav {
  @media (min-width: 768px) {
    display: none;
  }
}
</style>
